/**
 * DownlinkTracker - Tracks whether network weakness is due to downlink issues
 * Follows SRP by being responsible only for downlink-specific detection
 * Uses the same algorithm as NetworkHealthTracker but only considers downlink quality
 */
export class DownlinkTracker {
  private static readonly HISTORY_LIMIT = 20; // Number of recent samples to keep
  private static readonly BAD_THRESHOLD = 5; // Network quality 5–6 is considered poor (according to Agora's docs)
  private static readonly MIN_BAD_COUNT = 10; // Minimum consecutive bad samples to mark as weak
  private static readonly MIN_GOOD_COUNT = 10; // Minimum consecutive good samples to mark as recovered
  private static readonly BAD_RATIO_THRESHOLD = 0.5; // Percentage of bad samples in history to consider weak

  private history: number[] = [];
  private badCount = 0;
  private goodCount = 0;
  private isWeak = false;

  /**
   * Update downlink weakness status based on downlink quality only
   * @param downlinkQuality - Current downlink quality (1-6)
   * @returns true if downlink is weak, false otherwise
   */
  update(downlinkQuality: number): boolean {
    let score = downlinkQuality;
    // Treat score 0 as score 1 (best quality)
    if (score === 0) {
      score = 1;
    }

    this.history.push(score);
    if (this.history.length > DownlinkTracker.HISTORY_LIMIT) {
      this.history.shift();
    }

    if (this.history.length < DownlinkTracker.HISTORY_LIMIT) {
      return this.isWeak;
    }

    const badRatio = this.history.filter((v) => v >= DownlinkTracker.BAD_THRESHOLD).length / DownlinkTracker.HISTORY_LIMIT;

    if (score >= DownlinkTracker.BAD_THRESHOLD) {
      this.badCount++;
      this.goodCount = 0;
    } else {
      this.goodCount++;
      if (this.goodCount >= DownlinkTracker.MIN_GOOD_COUNT) {
        this.badCount = 0;
        this.isWeak = false;
      }
    }

    if (this.badCount >= DownlinkTracker.MIN_BAD_COUNT || badRatio >= DownlinkTracker.BAD_RATIO_THRESHOLD) {
      this.isWeak = true;
    }

    return this.isWeak;
  }
}
